import { memo } from 'react';
import type { BookingStats } from '../../lib/types';
import { SectionLabel, StatusDot } from '@perch/ui';

interface SummaryCardProps {
  stats: BookingStats;
  portfolioValue: number;
  lifetimeSavings: number;
}

/**
 * Portfolio-framed summary card with editorial typography.
 * Shows "X trips · watching $Y" to frame Perch as managing a
 * portfolio of travel spend. Lifetime savings appear after first rebook.
 *
 * Per the Perch Paradox: no check counts, no "checked X ago."
 * Trust compounds through portfolio value + watching duration (on cards).
 */
export const SummaryCard = memo(function SummaryCard({
  stats,
  portfolioValue,
  lifetimeSavings,
}: SummaryCardProps) {
  const activeCount = stats.active_bookings;
  const hasLifetimeSavings = lifetimeSavings > 0;

  return (
    <div className="relative overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-white to-stone-50 dark:from-stone-900 dark:via-stone-900 dark:to-stone-800 rounded-2xl" />

      <div className="relative border border-stone-200/60 dark:border-stone-700/50 rounded-2xl p-6 sm:p-8">
        {/* Main stats row */}
        <div className="flex items-end justify-between gap-6">
          {/* Left: Portfolio framing — trip count + total value */}
          <div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-5xl sm:text-6xl font-data tabular-nums text-stone-900 dark:text-stone-100 tracking-tight">
                {activeCount}
              </span>
              <span className="text-lg sm:text-xl text-stone-400 dark:text-stone-500 font-light">
                {activeCount === 1 ? 'trip' : 'trips'}
              </span>
              {portfolioValue > 0 && (
                <>
                  <span className="text-lg text-stone-300 dark:text-stone-600 font-light">·</span>
                  <span className="text-lg text-stone-400 dark:text-stone-500 font-light">
                    watching
                  </span>
                  <span className="text-lg font-data tabular-nums text-stone-900 dark:text-stone-100">
                    ${Math.round(portfolioValue).toLocaleString()}
                  </span>
                </>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <StatusDot />
              <span className="text-sm text-stone-500 dark:text-stone-400">
                Actively watching
              </span>
            </div>
          </div>

          {/* Right: Lifetime savings — only after first rebook */}
          {hasLifetimeSavings && (
            <div className="text-right">
              <SectionLabel className="mb-1">saved</SectionLabel>
              <div className="text-3xl sm:text-4xl font-data tabular-nums text-emerald-600 dark:text-emerald-400">
                ${Math.round(lifetimeSavings).toLocaleString()}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
});
